import React from 'react';
import { useProductBranding } from '../../../../../shared/hooks/useProductBranding';
import {
  RELIX_DEFAULT_ACCENT,
  resolveAutoContrast,
} from '../../../../../features/reports/export/colorUtils';

export interface LogoLivePreviewProps {
  productId: string | null;
}

/**
 * 3-up live preview of how the logo + brand color will look across the most
 * common Reports surfaces. Reflects logoStyle / brandColor changes immediately
 * so admins can pick a combination that reads well *before* anyone exports a
 * report.
 */
export const LogoLivePreview: React.FC<LogoLivePreviewProps> = ({ productId }) => {
  const branding = useProductBranding(productId);
  const logoUrl = branding.data?.logoUrl ?? null;
  const brandColor = branding.data?.brandColor ?? null;
  const logoStyle = branding.data?.logoStyle ?? 'auto_contrast';
  const accent = brandColor || RELIX_DEFAULT_ACCENT;

  return (
    <div className="rb-logo-preview">
      <div className="rb-logo-preview__card">
        <span className="rb-logo-preview__label">Cover</span>
        <div className="rb-logo-preview__cover" style={{ ['--rb-accent' as string]: accent }}>
          <LogoTile
            logoUrl={logoUrl}
            placeholder={branding.data?.id?.slice(0, 1)?.toUpperCase() || 'R'}
            applyStyle={resolveAutoContrast(logoStyle, 'cover', brandColor)}
            brandColor={accent}
            size="large"
          />
        </div>
      </div>
      <div className="rb-logo-preview__card">
        <span className="rb-logo-preview__label">Header</span>
        <div className="rb-logo-preview__header">
          <LogoTile
            logoUrl={logoUrl}
            placeholder={branding.data?.id?.slice(0, 1)?.toUpperCase() || 'R'}
            applyStyle={resolveAutoContrast(logoStyle, 'header', brandColor)}
            brandColor={accent}
            size="small"
          />
          <span className="rb-logo-preview__header-text">Product name</span>
        </div>
      </div>
      <div className="rb-logo-preview__card">
        <span className="rb-logo-preview__label">Dark surface</span>
        <div className="rb-logo-preview__dark">
          <LogoTile
            logoUrl={logoUrl}
            placeholder={branding.data?.id?.slice(0, 1)?.toUpperCase() || 'R'}
            applyStyle={resolveAutoContrast(logoStyle, 'dark-surface', brandColor)}
            brandColor={accent}
            size="medium"
          />
        </div>
      </div>
    </div>
  );
};

interface LogoTileProps {
  logoUrl: string | null;
  placeholder: string;
  applyStyle: 'transparent' | 'light_plate' | 'dark_plate';
  brandColor: string;
  size: 'small' | 'medium' | 'large';
}

const LogoTile: React.FC<LogoTileProps> = ({
  logoUrl,
  placeholder,
  applyStyle,
  brandColor,
  size,
}) => {
  const inner = logoUrl ? (
    <img src={logoUrl} alt="" className={`rb-logo-tile__img rb-logo-tile__img--${size}`} />
  ) : (
    <div
      className={`rb-logo-tile__placeholder rb-logo-tile__placeholder--${size}`}
      style={{ background: brandColor }}
    >
      {placeholder}
    </div>
  );
  if (applyStyle === 'transparent') return <div className="rb-logo-tile">{inner}</div>;
  return (
    <div className={`rb-logo-tile rb-logo-tile--${applyStyle.replace('_', '-')}`}>{inner}</div>
  );
};
